import { Button } from "@/components/ui/button"
import { Typography } from "@/components/ui/typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import Bold from "@tiptap/extension-bold"
import Document from "@tiptap/extension-document"
import HardBreak from "@tiptap/extension-hard-break"
import History from "@tiptap/extension-history"
import Text from "@tiptap/extension-text"
import { EditorContent, useEditor, type Editor } from "@tiptap/react"
import { BoldIcon, Download, FileText, Loader2, Maximize2 } from "lucide-react"
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { toastApiError } from "../../../../api/apiError"
import {
  useConformityDeclarationCss,
  useConformityDocumentBlocks,
  useSaveConformityDocumentBlockParagraph,
  type ConformityDocumentBlocksTree,
  type ConformityInlineNode,
} from "../../../../api/conformityApi"
import {
  DEFAULT_SHEET_GEOMETRY,
  geometryFrom,
  type SheetGeometry,
} from "../../../shared/paginate"
import { PaperScope } from "../../../shared/PaperScope"
import { ZoomControl } from "../../../shared/ZoomControl"
import { applyExternalContent } from "./applyExternalContent"
import { flattenPastedSlice } from "./flattenPastedSlice"
import {
  changedParagraphSlots,
  mergeExternalContent,
  paragraphSlotsOf,
} from "./paragraphSlotDiff"
import { SlotParagraph } from "./SlotParagraph"
import { SystemFragmentNode } from "./SystemFragmentNode"

const AUTOSAVE_DEBOUNCE_MS = 700
// A failed autosave is retried a bounded number of times, on a longer delay
// than the idle debounce — a transient blip shouldn't need the user to type
// again, but a slot that keeps failing must eventually stop hammering the
// server (it stays protected in `unconfirmedSlots` either way; see the
// review finding this responds to, 2026-09-21).
const MAX_SAVE_RETRIES = 3
const AUTOSAVE_RETRY_DELAY_MS = 2000
const SIGNATURE_FRAGMENT_KEY = "signatureBlock"

/** 2026-09-17 block-editor design: replaces the iframe preview for an open
 *  v2 draft. Renders the document as live, editable Tiptap content — system
 *  content (framework tables, annex, signature, header) as locked HTML
 *  fragments the backend already trusts, paragraph wording as real editable
 *  text. Edits never round-trip through a full document rebuild: Tiptap
 *  renders its own DOM directly, and only the changed paragraph(s) autosave,
 *  debounced, in the background.
 *
 *  Frozen/issued declarations never reach this component — the caller keeps
 *  routing those to FrozenPdfPane, unchanged. */
export function ConformityBlockEditorPanel({
  declarationId,
  label,
  className = "flex min-h-0 w-full flex-1 flex-col",
  containerRef,
  onDownload,
  downloadPending = false,
  onPreviewPdf,
  previewPdfLabel,
  scrollToSignatureOnLoad = false,
  onScrolledToSignature,
  canEdit,
  headerExtra,
}: {
  declarationId: string
  label?: string
  /** Beside the title in the toolbar (the v2 editor's template switcher). */
  headerExtra?: ReactNode
  className?: string
  /** The framework-list hover-highlight (design delta 7) queries `[data-fw]`
   *  elements inside this ref — no iframe here, so it targets the live DOM
   *  directly instead of an iframe's `contentDocument`. */
  containerRef?: React.Ref<HTMLDivElement>
  onDownload: () => void
  downloadPending?: boolean
  onPreviewPdf?: () => void
  previewPdfLabel?: string
  /** Armed right after a signature change; consumed on the NEXT successful
   *  refetch of the document tree (mirrors the old iframe panel's
   *  scroll-to-signature behaviour — see ConformityEditorModuleV2). */
  scrollToSignatureOnLoad?: boolean
  onScrolledToSignature?: () => void
  /** Same permission the autosave PATCH is guarded by, server-side and via
   *  useGuardedMutation — without this, a member lacking it could type
   *  into the document and only find out it never saved when the autosave
   *  silently no-ops. `false` makes Tiptap itself non-editable (no
   *  keystroke ever reaches ProseMirror), not just the toolbar. */
  canEdit: boolean
}) {
  const { translate } = useTranslation()
  const blocksQuery = useConformityDocumentBlocks(declarationId)
  const cssQuery = useConformityDeclarationCss()
  const saveParagraph = useSaveConformityDocumentBlockParagraph()
  // design §8: the sheets the preview paginates into use this declaration's
  // own page margins. A backend that doesn't send them yet falls back to the
  // stylesheet's own paper padding.
  const margins = blocksQuery.data?.pageGeometry?.marginsMm
  const geometry = useMemo<SheetGeometry>(
    () => (margins ? geometryFrom(margins) : DEFAULT_SHEET_GEOMETRY),
    [margins],
  )
  // design §8: the strips are never part of the editable tree, so the sheets
  // draw the very markup the PDF prints in those margins — today's legacy
  // footer included, which goes on EVERY declaration. Memoised: a fresh
  // object would re-subscribe `PaperScope`'s observers on every render.
  const pageHeaderHtml = blocksQuery.data?.pageHeaderHtml ?? null
  const pageFooterHtml = blocksQuery.data?.pageFooterHtml ?? null
  const stripHtml = useMemo(
    () => ({ header: pageHeaderHtml, footer: pageFooterHtml }),
    [pageHeaderHtml, pageFooterHtml],
  )

  if (blocksQuery.isLoading || cssQuery.isLoading) {
    return (
      <div className={className}>
        <PanelToolbar
          label={label}
          headerExtra={headerExtra}
          translate={translate}
        />
        <div className="flex min-h-0 flex-1 items-center justify-center bg-white text-[12px] text-grey5">
          <Loader2 size={14} className="mr-2 animate-spin" />
          {translate("MC_CS_CONF_EDIT_RENDERING")}
        </div>
      </div>
    )
  }

  if (blocksQuery.isError || !blocksQuery.data) {
    return (
      <div className={className}>
        <PanelToolbar
          label={label}
          headerExtra={headerExtra}
          translate={translate}
        />
        <div className="flex min-h-0 flex-1 items-center justify-center bg-white p-6 text-center text-[12px] text-red1">
          {translate("MC_CS_CONF_EDIT_PREVIEW_FAILED")}
        </div>
      </div>
    )
  }

  return (
    <BlockEditorBody
      declarationId={declarationId}
      label={blocksQuery.data.title || label}
      className={className}
      containerRef={containerRef}
      initialBlocks={blocksQuery.data.documentBlocks}
      latestBlocks={blocksQuery.data.documentBlocks}
      css={cssQuery.data ?? ""}
      geometry={geometry}
      stripHtml={stripHtml}
      onDownload={onDownload}
      downloadPending={downloadPending}
      onPreviewPdf={onPreviewPdf}
      previewPdfLabel={previewPdfLabel}
      saveParagraph={saveParagraph}
      scrollToSignatureOnLoad={scrollToSignatureOnLoad}
      onScrolledToSignature={onScrolledToSignature}
      canEdit={canEdit}
      headerExtra={headerExtra}
    />
  )
}

function PanelToolbar({
  label,
  headerExtra,
  translate,
  editor,
  canEdit,
  onPreviewPdf,
  previewPdfLabel,
  onDownload,
  downloadPending,
  zoom,
  onZoom,
}: {
  label?: string
  headerExtra?: ReactNode
  translate: ReturnType<typeof useTranslation>["translate"]
  editor?: Editor | null
  canEdit?: boolean
  onPreviewPdf?: () => void
  previewPdfLabel?: string
  onDownload?: () => void
  downloadPending?: boolean
  zoom?: number
  onZoom?: (next: number) => void
}) {
  return (
    <div className="flex items-center gap-3 border-b border-grey4 px-4 py-2">
      <span className="flex min-w-0 items-center gap-1.5 text-grey5">
        <FileText size={12} className="shrink-0" />
        <Typography as="small" className="truncate text-[12px] font-normal">
          {label ?? translate("MC_CS_CONF_EDIT_PREVIEW_LABEL")}
        </Typography>
      </span>
      {headerExtra}
      <span className="flex-1" />
      <div className="flex items-center gap-1.5">
        {editor && canEdit && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={translate("MC_CS_CONF_EDIT_ARIA_BOLD")}
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().toggleBold()}
            className={cn(
              "h-7 w-7 text-grey5 hover:text-white",
              editor.isActive("bold") && "bg-white/10 text-white",
            )}
          >
            <BoldIcon size={13} />
          </Button>
        )}
        {onPreviewPdf && (
          <Button
            type="button"
            variant="ghost"
            onClick={onPreviewPdf}
            className="mr-2 h-7 gap-1.5 rounded-md border border-yellow1/60 bg-yellow1/10 px-2.5 text-[12px] font-semibold text-yellow1 hover:bg-yellow1/20"
          >
            <Maximize2 size={12} />
            {previewPdfLabel}
          </Button>
        )}
        {onZoom && (
          <ZoomControl
            zoom={zoom ?? 100}
            onZoom={onZoom}
            className="flex items-center gap-1.5"
          />
        )}
        {onDownload && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={translate("MC_CS_CONF_EDIT_ARIA_DOWNLOAD_PDF")}
            onClick={onDownload}
            disabled={downloadPending}
            className="ml-2 h-7 w-7 text-grey5 hover:text-white"
          >
            {downloadPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Download size={14} />
            )}
          </Button>
        )}
      </div>
    </div>
  )
}

function BlockEditorBody({
  declarationId,
  label,
  className,
  containerRef,
  initialBlocks,
  latestBlocks,
  css,
  geometry,
  stripHtml,
  onDownload,
  downloadPending,
  onPreviewPdf,
  previewPdfLabel,
  saveParagraph,
  scrollToSignatureOnLoad,
  onScrolledToSignature,
  canEdit,
  headerExtra,
}: {
  headerExtra?: ReactNode
  declarationId: string
  label?: string
  className: string
  containerRef?: React.Ref<HTMLDivElement>
  initialBlocks: ConformityDocumentBlocksTree
  latestBlocks: ConformityDocumentBlocksTree
  css: string
  geometry: SheetGeometry
  stripHtml: { header: string | null; footer: string | null }
  onDownload: () => void
  downloadPending: boolean
  onPreviewPdf?: () => void
  previewPdfLabel?: string
  saveParagraph: ReturnType<typeof useSaveConformityDocumentBlockParagraph>
  scrollToSignatureOnLoad: boolean
  onScrolledToSignature?: () => void
  canEdit: boolean
}) {
  const { translate } = useTranslation()
  const rootRef = useRef<HTMLDivElement | null>(null)
  // Debounce scheduling only — a slot is removed from HERE the moment its
  // timer fires (the request starts), which is why dirtiness for the
  // merge below is tracked separately in `unconfirmedSlots`, not derived
  // from this map (review finding, 2026-09-21: a slot whose save was still
  // in flight, or had just failed, was briefly/indefinitely treated as
  // "clean" and could be overwritten by an incoming server refresh).
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  // Edited but not yet CONFIRMED SAVED — added the moment a change is
  // detected, removed only on a successful mutation response. This is the
  // source of truth `mergeExternalContent` reads: a slot stays protected
  // for the entire lifetime of an edit, through debounce, through the
  // in-flight request, and through any failed retries, not just while a
  // timer happens to be pending.
  const unconfirmedSlots = useRef<Set<string>>(new Set())
  // Consecutive-failure count per slot, purely to cap automatic retries;
  // reset whenever a genuinely new edit (not a retry) is made to that slot.
  const retryCounts = useRef<Map<string, number>>(new Map())
  // The last doc snapshot an update was diffed against — updated on every
  // keystroke, regardless of save success, so the diff always reflects only
  // what changed THIS transaction (see paragraphSlotDiff's module doc for
  // why this replaces `selection.$from.parent`).
  const lastSeenRef = useRef<ConformityDocumentBlocksTree>(initialBlocks)
  // A left-panel action's refetch that arrived while the user was mid-edit —
  // applied on blur instead of being dropped, so the user's own edit is
  // never overwritten mid-keystroke AND the refreshed system content is
  // never silently lost either.
  const pendingLatestRef = useRef<ConformityDocumentBlocksTree | null>(null)
  const saveParagraphRef = useRef(saveParagraph)
  saveParagraphRef.current = saveParagraph
  const [zoom, setZoom] = useState(100)

  const flushSlot = useCallback(
    (slot: string, editorInstance: Editor) => {
      const timer = timers.current.get(slot)
      if (timer) clearTimeout(timer)
      timers.current.delete(slot)
      const content = paragraphSlotsOf(editorInstance.getJSON()).get(slot)
      if (!content) {
        // Nothing left to protect (e.g. its framework was deselected mid-edit).
        unconfirmedSlots.current.delete(slot)
        retryCounts.current.delete(slot)
        return
      }
      saveParagraphRef.current.mutate(
        { id: declarationId, slot, content: content as ConformityInlineNode[] },
        {
          onSuccess: () => {
            unconfirmedSlots.current.delete(slot)
            retryCounts.current.delete(slot)
          },
          onError: (err) => {
            toastApiError(err, translate, "MC_CS_CONF_EDIT_TOAST_BLOCKS_ERR")
            // unconfirmedSlots keeps this slot protected regardless of retry
            // outcome — a transient failure must never make the slot look
            // "clean" to a later merge. Bounded automatic retry on top of
            // that, so a blip alone doesn't require the user to type again.
            const attempts = retryCounts.current.get(slot) ?? 0
            if (attempts < MAX_SAVE_RETRIES) {
              retryCounts.current.set(slot, attempts + 1)
              timers.current.set(
                slot,
                setTimeout(
                  () => flushSlot(slot, editorInstance),
                  AUTOSAVE_RETRY_DELAY_MS,
                ),
              )
            }
          },
        },
      )
    },
    [declarationId, translate],
  )

  const editor = useEditor({
    // Tiptap 3 defaults this to false; keep v2 behaviour (re-render on every
    // transaction) so render-time reads like editor.isActive() stay current.
    shouldRerenderOnTransaction: true,
    extensions: [
      Document,
      Text,
      SlotParagraph,
      Bold,
      HardBreak,
      History,
      SystemFragmentNode,
    ],
    content: initialBlocks,
    editable: canEdit,
    editorProps: {
      attributes: {
        class: "conformity-block-editor paper",
      },
      transformPasted: (slice, view) =>
        flattenPastedSlice(slice, view.state.schema),
    },
    onUpdate: ({ editor: e }) => {
      const nextDoc = e.getJSON() as unknown as ConformityDocumentBlocksTree
      const changed = changedParagraphSlots(lastSeenRef.current, nextDoc)
      lastSeenRef.current = nextDoc
      for (const slot of changed) {
        unconfirmedSlots.current.add(slot)
        retryCounts.current.delete(slot) // a fresh edit, not a retry
        const existing = timers.current.get(slot)
        if (existing) clearTimeout(existing)
        timers.current.set(
          slot,
          setTimeout(() => flushSlot(slot, e), AUTOSAVE_DEBOUNCE_MS),
        )
      }
    },
  })

  // Flush every still-unconfirmed slot immediately on unmount (e.g.
  // navigating away mid-edit, or right after a failed save whose retry
  // hasn't fired yet) rather than dropping it — the debounce is a UX
  // delay, not a discard window.
  useEffect(() => {
    const unconfirmed = unconfirmedSlots.current
    const timerMap = timers.current
    return () => {
      timerMap.forEach((t) => clearTimeout(t))
      timerMap.clear()
      if (!editor) return
      for (const slot of Array.from(unconfirmed)) {
        flushSlot(slot, editor)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- flush-on-unmount only, deliberately not re-run per render
  }, [editor])

  // Applies server-originated content (a left-panel recompute, another
  // slot's own autosave response) without ever discarding a slot that is
  // still `dirty` — edited but not yet CONFIRMED saved, tracked in
  // `unconfirmedSlots` for the entire edit/save/retry lifecycle, not just
  // while a debounce timer happens to be pending (mergeExternalContent) —
  // and without letting a plain Ctrl/Cmd-Z revert past it into stale
  // content (applyExternalContent; see its module doc for why
  // `editor.commands.setContent` alone is not enough). `lastSeenRef` is
  // updated to the MERGED result, not the raw incoming tree, so the next
  // onUpdate diff still only reports genuinely new changes.
  const applyLatest = useCallback(
    (editorInstance: Editor, tree: ConformityDocumentBlocksTree) => {
      const merged = mergeExternalContent(
        tree,
        editorInstance.getJSON() as unknown as ConformityDocumentBlocksTree,
        unconfirmedSlots.current,
      ) as ConformityDocumentBlocksTree
      lastSeenRef.current = merged
      applyExternalContent(editorInstance, merged)
    },
    [],
  )

  // A left-panel action (framework toggle, re-evaluate, …) invalidates and
  // refetches document-blocks; the derived tree already reflects this
  // declaration's stored edits, so applying it here is always safe EXCEPT
  // while the user is actively typing — deferred until focus leaves the
  // editor then, rather than yanking their cursor mid-keystroke or silently
  // dropping the refresh.
  useEffect(() => {
    if (!editor) return
    if (editor.isFocused) {
      pendingLatestRef.current = latestBlocks
      return
    }
    pendingLatestRef.current = null
    applyLatest(editor, latestBlocks)
  }, [editor, latestBlocks, applyLatest])

  useEffect(() => {
    if (!editor) return
    const dom = editor.view.dom
    const consumePending = () => {
      const pending = pendingLatestRef.current
      if (!pending) return
      pendingLatestRef.current = null
      applyLatest(editor, pending)
    }
    // Losing focus to another element on the SAME page fires a plain
    // element `blur`; switching browser tabs or applications does not —
    // `document.activeElement` never changes, so tiptap's own focus
    // tracking never fires either. `window`'s blur and the page becoming
    // hidden are the only signals for that case, so a refresh that arrives
    // while the user has simply tabbed away still reaches the editor once
    // they leave it, not only once they click somewhere else on the page.
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") consumePending()
    }
    dom.addEventListener("blur", consumePending)
    window.addEventListener("blur", consumePending)
    document.addEventListener("visibilitychange", onVisibilityChange)
    return () => {
      dom.removeEventListener("blur", consumePending)
      window.removeEventListener("blur", consumePending)
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  }, [editor, applyLatest])

  useEffect(() => {
    if (!scrollToSignatureOnLoad) return
    const el = rootRef.current?.querySelector(
      `[data-system-fragment-key="${SIGNATURE_FRAGMENT_KEY}"]`,
    )
    el?.scrollIntoView({ block: "center" })
    onScrolledToSignature?.()
  }, [scrollToSignatureOnLoad, onScrolledToSignature])

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      rootRef.current = node
      if (typeof containerRef === "function") containerRef(node)
      else if (containerRef && "current" in containerRef) {
        ;(
          containerRef as React.MutableRefObject<HTMLDivElement | null>
        ).current = node
      }
    },
    [containerRef],
  )

  return (
    <div className={className} ref={setRefs}>
      <PanelToolbar
        label={label}
        headerExtra={headerExtra}
        translate={translate}
        editor={editor}
        canEdit={canEdit}
        onPreviewPdf={onPreviewPdf}
        previewPdfLabel={previewPdfLabel}
        onDownload={onDownload}
        downloadPending={downloadPending}
        zoom={zoom}
        onZoom={setZoom}
      />
      <PaperScope
        css={css}
        extraCss={EDITOR_CHROME_CSS}
        geometry={geometry}
        stripHtml={stripHtml}
        className="min-h-0 flex-1 overflow-auto"
      >
        <div style={{ zoom: zoom !== 100 ? `${zoom}%` : undefined }}>
          <EditorContent editor={editor} />
        </div>
      </PaperScope>
    </div>
  )
}

// Editor-only affordances on top of the (scoped) print stylesheet. The paper
// framing itself — A4 width, page-margin padding, shadow, grey backdrop — is
// declaration_v2.css's own `@media screen` block, which the scope element
// above now receives as `.tpl-scope` / `.tpl-scope .paper`, so it is defined
// once for the PDF preview, this editor and the template editor alike.
const EDITOR_CHROME_CSS = `
.conformity-block-editor p[data-slot] {
  outline: none;
  cursor: text;
  border-radius: 3px;
}
/* The "yellow frame" the footer hint names: editable text is framed in the
 * brand yellow on hover and while typing. */
.conformity-block-editor p[data-slot]:hover {
  background: rgba(255, 244, 64, 0.1);
  box-shadow: 0 0 0 1px rgba(214, 196, 0, 0.55);
}
.conformity-block-editor p[data-slot]:focus {
  background: rgba(255, 244, 64, 0.16);
  box-shadow: 0 0 0 2px #FFF440;
}
.conformity-block-editor div[data-system-fragment] {
  pointer-events: none;
  user-select: text;
}
/* Framework-list hover-highlight (design delta 7) — same colors as the old
 * iframe-injected rule (ConformityEditorModuleV2's HL_OUTLINE/HL_BG); [data-fw]
 * now lives directly on this page instead of inside an iframe's srcDoc. */
[data-fw].fw-hl {
  outline: 2px solid #FFF440;
  outline-offset: 2px;
  background: #FFFDE5;
}
`
